/* ==========================================================================
   Yavru Patiler — Motion engine v2
   Drives what CSS scroll-driven animations can't (or where unsupported):
   parallax fallback · counters · magnetic · tilt · pinned scenes ·
   split-text headlines · scroll progress fallback.
   Respects <html data-yp-motion> + prefers-reduced-motion. Idempotent.
   ========================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  function intensity() { return html.getAttribute('data-yp-motion') || 'cinematic'; }
  function motionOff() { return reduced || intensity() === 'off'; }
  function cinematic() { return !reduced && intensity() === 'cinematic'; }

  var supportsViewTimeline = CSS.supports && CSS.supports('animation-timeline: view()');
  var supportsScrollTimeline = CSS.supports && CSS.supports('animation-timeline: scroll()');

  /* ---- rAF scroll loop (single listener for all scroll-driven JS) ---- */
  var scrollJobs = [];
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      for (var i = scrollJobs.length - 1; i >= 0; i--) {
        // prune jobs whose host left the DOM (theme editor reloads)
        if (scrollJobs[i].el && !scrollJobs[i].el.isConnected) { scrollJobs.splice(i, 1); continue; }
        // a single throwing job must not kill the loop (ticking would stick)
        try { scrollJobs[i].fn(); } catch (e) {}
      }
      ticking = false;
    });
  }
  function addScrollJob(fn, el) {
    scrollJobs.push({ fn: fn, el: el || null });
    fn();
    if (scrollJobs.length === 1) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
  }

  /* ---- 1. Parallax fallback (when no CSS view-timeline) ---- */
  function initParallax(root) {
    if (motionOff() || intensity() === 'minimal' || supportsViewTimeline) return;
    (root || document).querySelectorAll('.parallax__layer').forEach(function (layer) {
      if (layer.dataset.pjs) return;
      layer.dataset.pjs = '1';
      var speed = parseFloat(layer.getAttribute('data-speed') || '0.12');
      var host = layer.closest('.parallax') || layer.parentElement;
      layer.style.scale = '1.14';
      addScrollJob(function () {
        var r = host.getBoundingClientRect();
        var vh = window.innerHeight;
        if (r.bottom < -80 || r.top > vh + 80) return;
        var center = r.top + r.height / 2 - vh / 2;
        layer.style.setProperty('--py', (-center * speed).toFixed(1) + 'px');
      }, layer);
    });
  }

  /* ---- 2. Scroll progress fallback ---- */
  function initProgress() {
    var bar = document.querySelector('.scroll-progress__bar');
    if (!bar || motionOff() || supportsScrollTimeline || bar.dataset.pjs) return;
    bar.dataset.pjs = '1';
    addScrollJob(function () {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.setProperty('--scroll-p', max > 0 ? (window.scrollY / max).toFixed(4) : 0);
    });
  }

  /* ---- 3. Counters ---- */
  function animateCounter(el) {
    var raw = el.getAttribute('data-count-to') || '0';
    var target = parseFloat(raw.replace(/[^\d.]/g, '')) || 0;
    var prefix = el.getAttribute('data-count-prefix') || '';
    var suffix = el.getAttribute('data-count-suffix') || '';
    var dur = motionOff() ? 0 : 1600;
    var useDots = /\./.test(raw) === false && target >= 1000; // TR thousands: 15.000
    function fmt(v) {
      var n = Math.round(v);
      var s = useDots ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : n.toString();
      return prefix + s + suffix;
    }
    if (!dur) { el.textContent = fmt(target); return; }
    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(target * eased);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function initCounters(root) {
    var els = (root || document).querySelectorAll('[data-count-to]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(animateCounter); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { animateCounter(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { if (!el.dataset.cjs) { el.dataset.cjs = '1'; io.observe(el); } });
  }

  /* ---- 4. Magnetic buttons ---- */
  function initMagnetic(root) {
    if (!finePointer || !cinematic()) return;
    (root || document).querySelectorAll('.magnetic').forEach(function (btn) {
      if (btn.dataset.mjs) return;
      btn.dataset.mjs = '1';
      var strength = parseFloat(btn.getAttribute('data-magnet') || '0.25');
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        btn.style.setProperty('--mx', ((e.clientX - r.left - r.width / 2) * strength).toFixed(1) + 'px');
        btn.style.setProperty('--my', ((e.clientY - r.top - r.height / 2) * strength).toFixed(1) + 'px');
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.setProperty('--mx', '0px');
        btn.style.setProperty('--my', '0px');
      });
    });
  }

  /* ---- 5. Tilt cards (+ shine highlight) ---- */
  function initTilt(root) {
    if (!finePointer || !cinematic() || html.getAttribute('data-yp-tilt') === 'false') return;
    (root || document).querySelectorAll('.tilt').forEach(function (card) {
      if (card.dataset.tjs) return;
      card.dataset.tjs = '1';
      var max = parseFloat(card.getAttribute('data-tilt-max') || '7');
      if (!card.querySelector('.tilt__shine')) {
        var shine = document.createElement('span');
        shine.className = 'tilt__shine';
        shine.setAttribute('aria-hidden', 'true');
        card.appendChild(shine);
      }
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        card.style.setProperty('--ry', ((px - 0.5) * 2 * max).toFixed(2) + 'deg');
        card.style.setProperty('--rx', ((0.5 - py) * 2 * max).toFixed(2) + 'deg');
        card.style.setProperty('--shine-x', (px * 100).toFixed(1) + '%');
        card.style.setProperty('--shine-y', (py * 100).toFixed(1) + '%');
      });
      card.addEventListener('pointerleave', function () {
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      });
    });
  }

  /* ---- 6. Split-text headlines (word cascade on reveal) ---- */
  function initSplitText(root) {
    (root || document).querySelectorAll('[data-splittext]').forEach(function (el) {
      if (el.dataset.sjs) return;
      el.dataset.sjs = '1';
      if (motionOff()) { el.classList.add('split-done'); return; }
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach(function (w, i) {
        var outer = document.createElement('span');
        outer.className = 'split-word';
        var inner = document.createElement('span');
        inner.style.setProperty('--word-i', i);
        inner.textContent = w;
        outer.appendChild(inner);
        el.appendChild(outer);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'fade');
    });
  }

  /* ---- 7. Pinned scrollytelling scenes ---- */
  function initPinScenes(root) {
    (root || document).querySelectorAll('[data-pin-scene]').forEach(function (scene) {
      if (scene.dataset.pinjs) return;
      scene.dataset.pinjs = '1';
      var frames = scene.querySelectorAll('.pin-scene__frame');
      if (!frames.length) return;
      if (motionOff() || intensity() === 'minimal') { frames.forEach(function (f) { f.classList.add('is-active'); }); return; }
      scene.style.height = (frames.length * 100 + 60) + 'svh';
      var current = -1;
      addScrollJob(function () {
        var r = scene.getBoundingClientRect();
        var total = r.height - window.innerHeight;
        var p = Math.min(0.999, Math.max(0, -r.top / (total || 1)));
        var idx = Math.floor(p * frames.length);
        if (idx !== current) {
          current = idx;
          frames.forEach(function (f, i) {
            f.classList.toggle('is-active', i === idx);
            f.classList.toggle('is-past', i < idx);
          });
          var dots = scene.querySelectorAll('.pin-scene__dot');
          dots.forEach(function (d, i) {
            d.classList.toggle('is-active', i === idx);
            if (i === idx) d.setAttribute('aria-current', 'true');
            else d.removeAttribute('aria-current');
          });
        }
      }, scene);
    });
  }

  /* ---- 8. Horizontal scroll-pin process track ----
     Desktop + motion on: pin the section and translate [data-hscroll-track]
     left as the page scrolls, filling [data-hscroll-progress] and updating
     [data-hscroll-count]. Mobile / off / minimal / reduced-motion: leave the
     natural vertical layout untouched (never add the pinned class). */
  function initHScrollPin(root) {
    (root || document).querySelectorAll('[data-hscroll-pin]').forEach(function (pin) {
      if (pin.dataset.hsjs) return;
      pin.dataset.hsjs = '1';
      var track = pin.querySelector('[data-hscroll-track]');
      if (!track) return;
      var fill = pin.querySelector('[data-hscroll-progress]');
      var countEl = pin.querySelector('[data-hscroll-count]');
      var panels = track.children.length || 1;
      var desktop = window.matchMedia('(min-width: 768px) and (min-height: 640px)');
      var enabled = false;
      var maxShift = 0;

      function disable() {
        enabled = false;
        pin.classList.remove('process--pinned', 'is-scrolling');
        pin.style.height = '';
        track.style.transform = '';
        if (fill) fill.style.setProperty('--p', 0);
      }
      function layout() {
        if (!enabled) return;
        track.style.transform = 'none';
        maxShift = Math.max(0, track.scrollWidth - pin.clientWidth);
        pin.style.height = (maxShift + window.innerHeight) + 'px';
        render();
      }
      function render() {
        if (!enabled) return;
        var r = pin.getBoundingClientRect();
        var total = pin.offsetHeight - window.innerHeight;
        var p = Math.min(1, Math.max(0, -r.top / (total || 1)));
        track.style.transform = 'translate3d(' + (-p * maxShift).toFixed(1) + 'px,0,0)';
        if (fill) fill.style.setProperty('--p', p.toFixed(4));
        if (countEl) countEl.textContent = ('0' + Math.min(panels, Math.floor(p * panels) + 1)).slice(-2);
        pin.classList.toggle('is-scrolling', p > 0.012);
      }
      function evaluate() {
        var on = desktop.matches && !motionOff() && intensity() !== 'minimal';
        if (on && !enabled) { enabled = true; pin.classList.add('process--pinned'); layout(); }
        else if (!on && enabled) { disable(); }
        else if (enabled) { layout(); }
      }

      evaluate();
      addScrollJob(render, pin);
      window.addEventListener('resize', evaluate, { passive: true });
      if (desktop.addEventListener) desktop.addEventListener('change', evaluate);
    });
  }

  /* ---- 9. Scroll-scrubbed hero video ----
     [data-video-scrub]: cinematic mode pins the section and maps scroll
     progress to video.currentTime (lerped each frame for a smooth, Apple-style
     scrub). Markup ships autoplay+loop, so: minimal/off keep a normal looping
     video hero, no-JS stays in flow, and reduced-motion pauses the video. */
  function initVideoScrub(root) {
    (root || document).querySelectorAll('[data-video-scrub]').forEach(function (sec) {
      if (sec.dataset.vsjs) return;
      sec.dataset.vsjs = '1';
      var video = sec.querySelector('video');
      if (!video) return;

      // Serve the vertical film + poster on small screens (set before any load)
      var mobileSrc = video.getAttribute('data-mobile-src');
      if (mobileSrc && window.matchMedia('(max-width: 768px)').matches) {
        var mobilePoster = video.getAttribute('data-mobile-poster');
        if (mobilePoster) video.setAttribute('poster', mobilePoster);
        video.src = mobileSrc;
      }

      // Static mode: no scroll-scrub. Video just loops on its own (or pauses for
      // reduced-motion). Mobile source swap above still applies.
      if (sec.hasAttribute('data-video-static')) {
        if (reduced) {
          video.removeAttribute('autoplay');
          video.removeAttribute('loop');
          var stopStatic = function () { video.pause(); };
          if (video.readyState >= 2) stopStatic(); else video.addEventListener('loadeddata', stopStatic, { once: true });
        }
        return;
      }

      if (reduced) { // WCAG: no auto-animation; poster/paused frame stays
        video.removeAttribute('autoplay');
        video.removeAttribute('loop');
        var stop = function () { video.pause(); };
        if (video.readyState >= 2) stop(); else video.addEventListener('loadeddata', stop, { once: true });
        return;
      }
      if (!cinematic()) return; // minimal/off: looping video hero + pause toggle

      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      video.pause();
      video.preload = 'auto';
      try { video.load(); } catch (e) {}
      sec.classList.add('is-scrub');
      // pull the film up UNDER the chrome so it is truly fullscreen from the
      // first frame; the transparent header then floats over the film
      var chrome = document.querySelector('.header');
      var ann = document.querySelector('.announce');
      var pull = (chrome ? chrome.offsetHeight : 0) + (ann ? ann.offsetHeight : 0);
      if (pull) sec.style.marginTop = '-' + pull + 'px';

      var mult = (parseFloat(sec.getAttribute('data-scrub-length')) || 320) / 100;
      // svh is immune to the iOS URL-bar resize (px height rewrites yank the pin)
      var svh = CSS.supports && CSS.supports('height', '1svh');
      var lastW = window.innerWidth;
      function setH() { sec.style.height = svh ? (mult * 100) + 'svh' : Math.round(window.innerHeight * mult) + 'px'; }
      setH();
      window.addEventListener('resize', function () {
        if (window.innerWidth === lastW) return; // ignore URL-bar height-only resizes
        lastW = window.innerWidth;
        setH();
      }, { passive: true });

      var duration = 0;
      function meta() { duration = video.duration || 0; }
      if (video.readyState >= 1) meta(); else video.addEventListener('loadedmetadata', meta, { once: true });

      var FPS = 24; // matches the generated films; quantizing avoids sub-frame seek spam
      var target = 0, current = 0, animating = false, lastFrame = -1;
      function step() {
        if (!animating) return;
        current += (target - current) * 0.18;
        if (Math.abs(target - current) < 0.03) { current = target; animating = false; }
        if (duration && video.readyState >= 1) {
          var frame = Math.round(current * FPS);
          if (frame !== lastFrame) {
            lastFrame = frame;
            try { video.currentTime = frame / FPS; } catch (e) {}
          }
        }
        if (animating) requestAnimationFrame(step);
      }
      var chapterCount = sec.querySelectorAll('.hero-chapter').length;
      addScrollJob(function () {
        var r = sec.getBoundingClientRect();
        var total = sec.offsetHeight - window.innerHeight;
        var p = Math.min(1, Math.max(0, -r.top / (total || 1)));
        sec.style.setProperty('--p', p.toFixed(4));
        // header floats transparent while the film holds the screen
        document.documentElement.classList.toggle('yp-hero-mode', r.top <= 2 && r.bottom >= window.innerHeight - 2);
        // Apple-style chapters: equal scrub slices, text swaps at each boundary
        if (chapterCount) {
          var ci = Math.min(chapterCount - 1, Math.floor(p * chapterCount));
          if (sec.getAttribute('data-chapter') !== String(ci)) sec.setAttribute('data-chapter', String(ci));
        }
        if (duration) {
          target = Math.min(duration - 0.04, p * duration);
          if (!animating) { animating = true; requestAnimationFrame(step); }
        }
      }, sec);
    });
  }

  function init(root) {
    // Each feature is isolated: one throwing (e.g. on an odd element) must not
    // abort the rest of the motion system.
    [initSplitText, initParallax, initProgress, initCounters,
     initMagnetic, initTilt, initPinScenes, initHScrollPin, initVideoScrub].forEach(function (fn) {
      try { fn(root); } catch (e) {
        if (window.console && console.warn) console.warn('[yp-motion] ' + (fn.name || 'step') + ' failed:', e);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
